import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Search, Plus, Download } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { UserList } from '../../components/admin/users/UserList';
import { UserForm } from '../../components/admin/users/UserForm';
import { UserImport } from '../../components/admin/users/UserImport';
import { BackButton } from '../../components/common/BackButton';
import { useUserManagement } from '../../hooks/admin/useUserManagement';

export function UserManagement() {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const { users, isLoading, updateUser, deleteUser, refreshUsers } = useUserManagement();

  const handleExportUsers = () => {
    if (!users.length) {
      toast.error('No users to export');
      return;
    }

    // Create CSV content
    const headers = ['Email', 'First Name', 'Last Name', 'Admin Status', 'Career Level', 'Location', 'Created At', 'Last Updated'];
    const csvContent = [
      headers.join(','),
      ...users.map(user => {
        // Escape fields that may contain commas
        const escapeCsvField = (field: string) => {
          if (!field) return '""';
          // Escape quotes and wrap in quotes if contains special chars
          if (field.includes('"') || field.includes(',') || field.includes('\n')) {
            return `"${field.replace(/"/g, '""')}"`;
          }
          return field;
        };

        return [
          escapeCsvField(user.email || ''),
          escapeCsvField(user.first_name || ''),
          escapeCsvField(user.last_name || ''),
          user.is_admin ? 'Yes' : 'No',
          escapeCsvField(user.career_level || ''),
          escapeCsvField(user.location || ''),
          new Date(user.created_at).toISOString().split('T')[0],
          new Date(user.updated_at).toISOString().split('T')[0]
        ].join(',');
      })
    ].join('\n');

    // Create and download file
    // Add BOM for Excel UTF-8 compatibility
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `tezhly_users_${new Date().toISOString().split('T')[0]}.csv`);
    try {
      document.body.appendChild(link);
      link.click();
      toast.success('Users exported successfully');
    } catch (error) {
      console.error('Error exporting users:', error);
      toast.error('Failed to export users');
    } finally {
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  };

  const filteredUsers = users.filter(user => 
    user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.first_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.last_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#ddd9d9] py-8">
      <div className="max-w-7xl mx-auto px-4">
        <BackButton to="/admin" label="Back to Admin Dashboard" />

        <div className="flex justify-between items-center mb-8">
          <h1 className="text-2xl font-bold text-black">User Management</h1>
          <div className="flex items-center gap-4">
            <button
              onClick={handleExportUsers}
              className="px-4 py-2 bg-[#373737] text-white rounded-xl hover:opacity-90 transition-colors flex items-center"
            >
              <Download className="h-5 w-5 mr-2" />
              Export Users
            </button>
            <button
              onClick={() => setIsImportOpen(true)}
              className="px-4 py-2 bg-[#373737] text-white rounded-xl hover:opacity-90 transition-colors flex items-center"
            >
              <Plus className="h-5 w-5 mr-2" />
              Import Users
            </button>
            <button
              onClick={() => setIsFormOpen(true)}
              className="btn-primary flex items-center"
            >
              <Plus className="h-5 w-5 mr-2" />
              Add User
            </button>
          </div>
        </div>

        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Search users..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-white border border-gray-200 rounded-xl text-black placeholder-gray-400 focus:ring-2 focus:ring-accent-green focus:border-accent-green"
            />
          </div>
        </div>

        <UserList 
          users={filteredUsers}
          isLoading={isLoading}
          onUpdate={updateUser}
          onDelete={deleteUser}
          onRefresh={refreshUsers}
        />

        {isFormOpen && (
          <UserForm
            onClose={() => setIsFormOpen(false)}
            onSuccess={() => {
              setIsFormOpen(false);
              refreshUsers();
            }}
          />
        )}
        
        {isImportOpen && (
          <UserImport
            onClose={() => setIsImportOpen(false)}
            onSuccess={() => {
              setIsImportOpen(false);
              refreshUsers();
            }}
          />
        )}
      </div>
    </div>
  );
}